'use client';
import { motion } from 'motion/react';
import React, { useState } from 'react';

import RichText from '@/components/RichText';
import type { TextElementBlock as TextElementBlockProps } from '@/payload-types';
import { cn } from '@/utils/cn';
import { splitText } from '@/utils/splitText';

import { ButtonGroupBlock } from '../ButtonGroupBlock/Component';
import styles from './block.module.scss';

export const TextElementBlock: React.FC<TextElementBlockProps> = (props) => {
  const {
    alignment,
    animate,
    buttons,
    content,
    heading,
    headingAppearance,
    headingTag,
    inverted,
    subheading,
    subheadingAppearance,
  } = props;

  const HeadingTagElement = headingTag || 'h2';

  const [headingDone, setHeadingDone] = useState(false);

  // Variants for the container
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  // Variants for the fade-up animation
  const fadeUpVariant = (dependsOn: boolean) => ({
    hidden: { opacity: 0, y: 20 },
    visible: dependsOn ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 },
    noAnimation: { opacity: 1, y: 0 },
  });

  return (
    <motion.div
      className={cn(
        styles.textElement,
        alignment && styles[alignment],
        inverted && inverted === 'light' && styles.inverted
      )}
      initial={!animate ? 'visible' : 'hidden'}
      whileInView='visible'
      viewport={{ once: true }}
      variants={containerVariants}
    >
      {subheading && (
        <motion.div
          initial={!animate ? 'noAnimation' : 'hidden'}
          animate={animate ? 'visible' : 'noAnimation'}
          style={{ willChange: 'opacity, transform' }}
          variants={fadeUpVariant(headingDone)}
        >
          <span
            className={cn(
              styles.subheading,
              subheadingAppearance ? subheadingAppearance : 'd6'
            )}
          >
            {animate
              ? splitText({
                  alignment,
                  inverted,
                  text: subheading,
                })
              : subheading}
          </span>
        </motion.div>
      )}
      {heading && (
        <motion.div
          initial={!animate ? 'noAnimation' : 'hidden'}
          animate={animate ? 'visible' : 'noAnimation'}
          style={{ willChange: 'opacity, transform' }}
          variants={fadeUpVariant(true)}
        >
          <HeadingTagElement
            className={cn(
              styles.heading,
              headingAppearance ? headingAppearance : 'h2'
            )}
          >
            {animate
              ? splitText({
                  alignment,
                  inverted,
                  text: heading,
                  onComplete: () => setHeadingDone(true),
                })
              : heading}
          </HeadingTagElement>
        </motion.div>
      )}
      {content && (
        <motion.div
          initial={!animate ? 'noAnimation' : 'hidden'}
          animate={animate ? 'visible' : 'noAnimation'}
          style={{ willChange: 'opacity, transform' }}
          variants={fadeUpVariant(headingDone)}
        >
          <RichText content={content} enableGutter={false} />
        </motion.div>
      )}
      {buttons && buttons.length > 0 && (
        <motion.div
          initial={!animate ? 'noAnimation' : 'hidden'}
          animate={animate ? 'visible' : 'noAnimation'}
          style={{ willChange: 'opacity, transform' }}
          variants={fadeUpVariant(headingDone)}
        >
          <ButtonGroupBlock
            alignment={alignment}
            buttons={buttons}
            blockType='buttonGroup'
          />
        </motion.div>
      )}
    </motion.div>
  );
};
